import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Avatar,
  IconButton,
  Badge,
  Button,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  Email as EmailIcon,
  PhoneAndroid as PhoneAndroidIcon,
  LocationCity as LocationCityIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  Hotel as HotelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../Config';
import { CircularProgress } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import Swal from 'sweetalert2';

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'confirmed':
    case 'checked-in':
      return 'success';
    case 'pending':
      return 'warning';
    case 'cancelled':
      return 'error';
    default:
      return 'info';
  }
};

export default function ReservationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [reservation, setReservation] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasDigiLock, setDigiLock] = useState(false);
  const [passKey, setPassKey] = useState({});
  const [showPassKey, setShowPassKey] = useState(null);
  const [passKeyLoading, setPassKeyLoading] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchReservation = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.apiBaseUrl}/admin/reservation/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch reservation');

        setReservation(data.booking);
        setGuests(Object.values(data.booking.guestList || {}));
        if (data.booking.hasDigiLock === true) {
          setDigiLock(true);
        } else {
          setDigiLock(false);
        }
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    fetchReservation();
  }, [id]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getGuestByRoomID = (roomID) => guests.find((g) => g.roomID === roomID);

  const hasPendingPasscode = hasDigiLock && guests.some((g) => g.isTTPasscodeGenerate === false);

  const fetchPassKey = async (reservationID, roomID) => {
    if (!reservationID || !roomID) return;

    setPassKeyLoading(roomID);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.apiBaseUrl}/admin/get-pass-key/${reservationID}/${roomID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Failed to fetch pass key');
      }

      const password = data?.data?.password || 'N/A';

      setPassKey((prev) => ({ ...prev, [roomID]: password }));
      setShowPassKey(roomID);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error Fetching Pass Key',
        text: err.message || 'Something went wrong while fetching the pass key.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setPassKeyLoading(null);
    }
  };

  const handleGeneratePassKey = async (reservationID, roomID) => {
    if (!reservationID || !roomID) return;

    setGenerateLoading(roomID);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.apiBaseUrl}/admin/get-pass-key/${reservationID}/${roomID}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Failed to generate pass key');
      }

      const password = data?.data?.password || 'N/A';

      setPassKey((prev) => ({ ...prev, [roomID]: password }));
      setShowPassKey(roomID);

      setGuests((prev) =>
        prev.map((g) => (g.roomID === roomID ? { ...g, isTTPasscodeGenerate: true } : g))
      );

      Swal.fire({
        icon: 'success',
        title: 'Pass Key Generated',
        text: 'TT lock pass key has been generated successfully!',
        confirmButtonColor: '#10b981',
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error Generating Pass Key',
        text: err.message || 'Something went wrong while generating the pass key.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setGenerateLoading(null);
    }
  };

  const handleEditPassKey = async (reservationID, roomID) => {
    const { value: passcode } = await Swal.fire({
      title: 'Enter New Passcode',
      html: `
        <div style="
          display: flex; 
          justify-content: center; 
          gap: 10px; 
          margin-top: 10px;
        ">
          <input id="digit1" type="text" maxlength="1" class="swal-otp-input" />
          <input id="digit2" type="text" maxlength="1" class="swal-otp-input" />
          <input id="digit3" type="text" maxlength="1" class="swal-otp-input" />
          <input id="digit4" type="text" maxlength="1" class="swal-otp-input" />
        </div>
        <style>
          .swal-otp-input {
            width: 50px;
            height: 55px;
            font-size: 26px;
            text-align: center;
            border-radius: 8px;
            border: 2px solid #d1d5db;
            outline: none;
            transition: 0.2s;
          }
          .swal-otp-input:focus {
            border-color: #6366f1;
            box-shadow: 0 0 4px rgba(99,102,241,0.6);
          }
          @media(max-width: 450px) {
            .swal-otp-input {
              width: 40px;
              height: 50px;
              font-size: 22px;
            }
          }
        </style>
      `,
      confirmButtonText: 'Update',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#ef4444',

      didOpen: () => {
        const inputs = [
          document.getElementById('digit1'),
          document.getElementById('digit2'),
          document.getElementById('digit3'),
          document.getElementById('digit4'),
        ];

        inputs[0].focus();

        inputs.forEach((input, index) => {
          input.addEventListener('input', () => {
            input.value = input.value.replace(/\D/, '');

            if (input.value && index < 3) {
              inputs[index + 1].focus();
            }
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Backspace' && !input.value && index > 0) {
              inputs[index - 1].focus();
            }
          });
        });
      },

      preConfirm: () => {
        const d1 = document.getElementById('digit1').value;
        const d2 = document.getElementById('digit2').value;
        const d3 = document.getElementById('digit3').value;
        const d4 = document.getElementById('digit4').value;

        const fullCode = d1 + d2 + d3 + d4;

        if (!/^\d{4}$/.test(fullCode)) {
          Swal.showValidationMessage('Please enter a valid 4-digit passcode');
          return false;
        }

        return fullCode;
      },
    });

    if (!passcode) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.apiBaseUrl}/admin/update-pass-key/${reservationID}/${roomID}/${passcode}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Failed to update passkey');

      Swal.fire({
        icon: 'success',
        title: 'Passkey Updated',
        text: 'New passkey has been set successfully!',
        confirmButtonColor: '#10b981',
      });

      fetchPassKey(reservationID, roomID);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error Updating Passkey',
        text: err.message || 'Something went wrong.',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSendEmail = async (reservationId, type) => {
    handleMenuClose();
    setEmailLoading(true);

    try {
      const token = localStorage.getItem('token');

      const res = await fetch(`${config.apiBaseUrl}/admin/send-reminder`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          reservationID: reservationId,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setSnackbarSeverity('error');
        setSnackbarMessage(data.message || 'Failed to send email');
        setSnackbarOpen(true);
        return;
      }

      setSnackbarSeverity('success');
      setSnackbarMessage(`${type} email sent successfully!`);
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Network error, please try again.');
      setSnackbarOpen(true);
    } finally {
      setEmailLoading(false);
    }
  };

  const getPaymentStatus = (balance, paid) => {
    if (balance === 0) return { label: 'Paid', color: 'success' };
    if (paid > 0) return { label: 'Partial', color: 'warning' };
    return { label: 'Unpaid', color: 'error' };
  };

  const isPaid = (balanceDetailed = {}) => {
    const paid = Number(balanceDetailed.paid || 0);
    const grandTotal = Number(balanceDetailed.grandTotal || 0);

    return paid >= grandTotal && grandTotal > 0;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        <CircularProgress size={48} thickness={4} color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 4, bgcolor: 'error.light' }}>
        <Typography color="error.main" variant="h6" gutterBottom>
          Error
        </Typography>
        <Typography>{error}</Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  if (!reservation) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 4 }}>
        <Typography>No reservation found.</Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Reservation Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {reservation?.reservationID || 'N/A'} • {reservation?.guestName || 'Guest'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {reservation?.propertyName}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="column" alignItems="flex-end" spacing={1} sx={{ width: 140 }}>
            <Chip
              label={reservation.status}
              color={getStatusColor(reservation.status)}
              sx={{
                width: '100%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                textTransform: 'capitalize',
                justifyContent: 'center',
              }}
            />

            <Button
              variant="contained"
              onClick={handleMenuOpen}
              disabled={emailLoading}
              sx={{
                width: '100%',
                backgroundColor: '#ffffff',
                color: '#764ba2',
                fontWeight: 600,
                borderRadius: '10px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#f3e8ff' },
              }}
            >
              {emailLoading ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                'Send Email'
              )}
            </Button>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem
                disabled={emailLoading}
                onClick={() => handleSendEmail(reservation.reservationID, 'welcome')}
              >
                Send Welcome Mail
              </MenuItem>
              <MenuItem
                disabled={emailLoading}
                onClick={() => handleSendEmail(reservation.reservationID, 'reminder')}
              >
                Send Reminder Mail
              </MenuItem>
              <MenuItem
                disabled={emailLoading}
                onClick={() => handleSendEmail(reservation.reservationID, 'credentials')}
              >
                Send Credentials Mail
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
      </Box>

      {hasPendingPasscode && (
        <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
          TT lock pass key has not been generated for one or more guests yet. Please generate the
          pass key to enable room access.
        </Alert>
      )}

      <Box sx={{ mt: '20px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} maxWidth={400} sm={6} md={4} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 1,
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                border: '2px solid',
                borderColor: 'primary.100',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                  borderColor: 'primary.main',
                },
              }}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      color: 'white',
                    }}
                  >
                    <CalendarTodayIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      Reservation Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Booking details and timeline
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={3} flexGrow={1}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'primary.100',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="primary.main"
                      fontWeight="bold"
                      sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                    >
                      Reservation ID
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                      {reservation.reservationID}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'success.50',
                          border: '1px solid',
                          borderColor: 'success.200',
                          textAlign: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="success.main"
                          fontWeight="bold"
                          sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                          Check-in
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {formatDate(reservation.startDate)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(reservation.startDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                          })}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'warning.50',
                          border: '1px solid',
                          borderColor: 'warning.200',
                          textAlign: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="warning.main"
                          fontWeight="bold"
                          sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                          Check-out
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {formatDate(reservation.endDate)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(reservation.endDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                          })}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'white',
                      border: '2px solid',
                      borderColor: isPaid(reservation.balanceDetailed)
                        ? 'success.light'
                        : 'warning.light',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="bold"
                      sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                    >
                      Payment Status
                    </Typography>

                    <Box sx={{ mt: 1, mb: 2 }}>
                      <Chip
                        label={isPaid(reservation.balanceDetailed) ? 'Paid' : 'Pending'}
                        color={isPaid(reservation.balanceDetailed) ? 'success' : 'warning'}
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          px: 2,
                        }}
                      />
                    </Box>

                    <Stack spacing={1}>
                      <PaymentRow label="Subtotal" value={reservation.balanceDetailed.subTotal} />
                      <PaymentRow
                        label="Additional Items"
                        value={reservation.balanceDetailed.additionalItems}
                      />
                      <PaymentRow
                        label="Taxes & Fees"
                        value={reservation.balanceDetailed.taxesFees}
                      />

                      <Divider />

                      <PaymentRow
                        label="Grand Total"
                        value={reservation.balanceDetailed.grandTotal}
                        bold
                      />
                      <PaymentRow
                        label="Paid"
                        value={reservation.balanceDetailed.paid}
                        color="success.main"
                      />
                      <PaymentRow
                        label="Balance Due"
                        value={reservation.balance}
                        color={reservation.balance > 0 ? 'error.main' : 'success.main'}
                        bold
                      />
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4} maxWidth={380} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                maxWidth: 400,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 1,
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                border: '2px solid',
                borderColor: 'success.100',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                  borderColor: 'success.main',
                },
              }}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'success.main',
                      color: 'white',
                    }}
                  >
                    <HotelIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      Room & Guest Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Accommodation and contact information
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'success.100',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          color="success.main"
                          fontWeight="bold"
                          sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                          Guest Name
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                          {reservation.guestName}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Email */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'success.100',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="success.main"
                      fontWeight="bold"
                      sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                    >
                      Contact Email
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {reservation.guestEmail}
                      </Typography>
                    </Stack>
                  </Box>

                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'info.50',
                        border: '1px solid',
                        borderColor: 'info.200',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 0.5,
                      }}
                    >
                      <HotelIcon color="info" sx={{ fontSize: 28 }} />
                      <Typography
                        variant="caption"
                        color="info.main"
                        fontWeight="bold"
                        sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                      >
                        Room Type
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {reservation.assigned?.[0]?.roomTypeName || 'Not Assigned'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'secondary.50',
                        border: '1px solid',
                        borderColor: 'secondary.200',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 0.5,
                      }}
                    >
                      <LocationCityIcon color="secondary" sx={{ fontSize: 28 }} />
                      <Typography
                        variant="caption"
                        color="secondary.main"
                        fontWeight="bold"
                        sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                      >
                        Room Number
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {reservation.assigned?.[0]?.roomName || 'TBD'}
                      </Typography>
                    </Box>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {hasDigiLock && (
            <Grid item xs={12} md={4} maxWidth={380} sx={{ display: 'flex' }}>
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 1,
                  background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                  border: '2px solid',
                  borderColor: 'warning.100',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                    borderColor: 'warning.main',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'warning.main',
                        color: 'white',
                      }}
                    >
                      <VpnKeyIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="warning.main">
                        Room Pass Keys
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Access credentials for your assigned rooms
                      </Typography>
                    </Box>
                  </Stack>

                  {reservation.assigned?.length > 0 ? (
                    <Stack spacing={2}>
                      {reservation.assigned.map((room) => {
                        const guestForRoom = getGuestByRoomID(room.roomID);
                        const passcodeGenerated = guestForRoom?.isTTPasscodeGenerate !== false;

                        return (
                          <Box
                            key={room.roomID}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: 'white',
                              border: '1px solid',
                              borderColor: 'warning.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="warning.main"
                                fontWeight="bold"
                                noWrap
                                sx={{ display: 'block' }}
                              >
                                Room Pass Key ({room.roomName})
                              </Typography>

                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{
                                  mt: 0.5,
                                  userSelect: 'none',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {!passcodeGenerated
                                  ? 'Not Generated'
                                  : passKeyLoading === room.roomID
                                    ? 'Loading...'
                                    : showPassKey === room.roomID
                                      ? passKey[room.roomID] || 'N/A'
                                      : '*****'}
                              </Typography>
                            </Box>

                            {passcodeGenerated ? (
                              <>
                                <IconButton
                                  onClick={() => {
                                    if (showPassKey === room.roomID) {
                                      setShowPassKey(null);
                                    } else {
                                      fetchPassKey(reservation.reservationID, room.roomID);
                                    }
                                  }}
                                >
                                  {showPassKey === room.roomID ? (
                                    <VisibilityOffIcon color="action" />
                                  ) : (
                                    <VisibilityIcon color="warning" />
                                  )}
                                </IconButton>

                                <IconButton
                                  onClick={() =>
                                    handleEditPassKey(reservation.reservationID, room.roomID)
                                  }
                                >
                                  <EditIcon color="primary" />
                                </IconButton>
                              </>
                            ) : (
                              <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                }}
                                disabled={generateLoading === room.roomID}
                                onClick={() =>
                                  handleGeneratePassKey(reservation.reservationID, room.roomID)
                                }
                              >
                                {generateLoading === room.roomID ? (
                                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                                ) : (
                                  'Generate Pass Key'
                                )}
                              </Button>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No rooms assigned yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      <Typography variant="h6" mb={3} mt={3} color="primary.main" fontWeight="bold">
        👥 Guest Information ({guests.length} {guests.length === 1 ? 'Guest' : 'Guests'})
      </Typography>

      <Grid container spacing={3} alignItems="stretch">
        {guests.map((g, i) => {
          const initials =
            `${g.guestFirstName?.[0] || ''}${g.guestLastName?.[0] || ''}`.toUpperCase();

          return (
            <Grid item xs={12} sm={6} lg={4} key={g._id || g.guestID}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    borderColor: 'primary.main',
                  },
                  background: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={2} mb={3}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 56,
                          height: 56,
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          border: '3px solid white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                      >
                        {initials || <PersonIcon />}
                      </Avatar>
                      <Badge
                        badgeContent={i + 1}
                        color="primary"
                        sx={{
                          position: 'absolute',
                          bottom: -4,
                          right: -4,
                          '& .MuiBadge-badge': {
                            fontSize: '0.75rem',
                            minWidth: 20,
                            height: 20,
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {g.guestFirstName} {g.guestLastName}
                      </Typography>
                      <Chip
                        label={g.guestStatus}
                        color={getStatusColor(g.guestStatus)}
                        size="small"
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: 'medium',
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                  </Stack>

                  {/* Contact Info */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="text.secondary"
                      mb={1.5}
                    >
                      Contact Information
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                          transition: 'all 0.2s ease',
                          '&:hover': { bgcolor: 'grey.100' },
                        }}
                      >
                        <EmailIcon fontSize="small" color="primary" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {g.guestEmail || 'Not provided'}
                          </Typography>
                        </Box>
                        {g.guestEmail && (
                          <IconButton
                            size="small"
                            sx={{ ml: 'auto' }}
                            onClick={() => window.open(`mailto:${g.guestEmail}`)}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <PhoneAndroidIcon fontSize="small" color="primary" />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{g.guestPhone || 'Not provided'}</Typography>
                        </Box>
                        {g.guestPhone && (
                          <IconButton
                            size="small"
                            sx={{ ml: 'auto' }}
                            onClick={() => window.open(`tel:${g.guestPhone}`)}
                          >
                            <PhoneAndroidIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Stack>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="text.secondary"
                      mb={1.5}
                    >
                      Location Details
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <LocationCityIcon fontSize="small" color="secondary" />
                        <Typography variant="body2">
                          {g.guestCity || 'City not provided'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <PublicIcon fontSize="small" color="secondary" />
                        <Typography variant="body2">
                          {g.guestCountry || 'Country not provided'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Guest #{i + 1} • Added {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

const PaymentRow = ({ label, value, bold = false, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" fontWeight={bold ? 'bold' : 500} color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 'bold' : 600} color={color || 'text.primary'}>
      £ {Number(value).toFixed(2)}
    </Typography>
  </Box>
);
